import React, { useState, useEffect, memo, useRef, useCallback } from "react"

import OutsideClickHandler from "react-outside-click-handler"
import { debounce } from "lodash"
import { $getRoot } from "lexical"

import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary"
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin"
import { ContentEditable } from "@lexical/react/LexicalContentEditable"
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin"
import { ListPlugin } from "@lexical/react/LexicalListPlugin"
import { ToolbarPlugin } from "./plugins/ToolbarPlugin"
import { useSharedHistoryContext } from "./context/SharedHistory"
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin"
import { EditorComposer } from "./context/LexicalComposer"
import ListMaxIndentLevelPlugin from "@editor/plugins/ListMaxIndentPlugin"
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin"
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin"
import { TRANSFORMERS } from "@lexical/markdown"
import { Box, Button, Text } from "@mos-cat/ds"

// import { useTaskContext } from "@components/contexts/TaskContext"
import ImagesPlugin from "@src/components/form/Editor/plugins/ImagesPlugin"
import { Menu, MenuItem } from "./components/CustomMenu"

import styles from "./editor.module.css"
import { OnChangePlugin } from "./plugins/OnChangePlugin"
import TableOfContentPlugin from "@editor/plugins/TableOfContentPlugin"
import CodeHighlightPlugin from "@editor/plugins/CodeHighlightPlugin"
import { MentionsPlugin } from "@editor/plugins/MentionPlugin"

const debouncedOnUpdate = debounce(async (state, onUpdate) => {
  onUpdate?.(state)
}, 500)

const onChange = (state, setPlainText, setJsonState, setIsClear, setIsHasValue, onUpdate) => {
  state.read(() => {
    const clearText = $getRoot().getTextContent()
    const isHasTextContent = state?._nodeMap?.size > 2
    setIsHasValue(isHasTextContent)

    if (state.isEmpty()) {
      setPlainText("")
      setJsonState(undefined)
      setIsClear(false)
    } else {
      setIsClear(true)
      setPlainText(clearText)
      setJsonState(state)
    }

    debouncedOnUpdate(
      { text: clearText, jsonText: state.isEmpty() ? "" : JSON.stringify(state) },
      onUpdate,
    )
  })
}

export const CommentEditor = memo(
  ({
    id,
    onSave,
    onCancel,
    onBlur,
    content,
    readOnly,
    disabled,
    onUpdate,
    isCheckEqual = true,
    currentPlainText = "",
    type = "description",
  }) => {
    const { historyState } = useSharedHistoryContext()
    const [jsonState, setJsonState] = useState(content)
    const [isClear, setIsClear] = useState(false)
    const [isHasValue, setIsHasValue] = useState(false)
    const [hasFocus, setHasFocus] = useState(false)
    const [isDragging, setIsDragging] = useState(false)
    const [ismenuOpen, setIsMenuOpen] = useState(false)
    const [plainText, setPlainText] = useState(currentPlainText)
    const [isStopEditorUpdate, setIsStopEditorUpdate] = useState(false)
    //функционал для onBlur
    const editorRef = useRef(null)
    // const {
    //   isDragging: isTaskDragging,
    //   setIsDragging: setIsTaskDragging,
    //   setIsEditorActive,
    // } = useTaskContext()

    const [containerSize, setContainerSize] = useState({ width: 0, height: 0, top: 0 })

    const handleOverlayClick = useCallback(() => {
      if (onBlur && jsonState !== content && !onSave) {
        const newComment = JSON.stringify(jsonState)
        if (!isCheckEqual || JSON.stringify(content) !== newComment) {
          onBlur(newComment, plainText)
        }
        onCancel()
      }
    }, [content, jsonState, onBlur, onCancel, onSave])

    const onOutsideClick = (event) => {
      if (editorRef.current && !editorRef.current.contains(event.target) && !isStopEditorUpdate) {
        handleOverlayClick()
      }
    }

    const handleSave = () => {
      onSave(plainText, JSON.stringify(jsonState))
      // Скрыто, тк при сохранении с ошибкой нельзя нажать и попробовать сохранить снова
      // setJsonState(undefined)
    }

    const handleCancel = () => {
      setJsonState(undefined)
      onCancel()
    }

    useEffect(() => {
      if (containerSize.height !== editorRef.current.clientHeight) {
        setContainerSize({
          width: editorRef.current.clientWidth,
          height: editorRef.current.clientHeight,
          top: onBlur ? editorRef.current.offsetTop : 0,
        })
      }
    }, [onBlur])

    // useEffect(() => {
    //   if (isDragging) {
    //     setIsTaskDragging(false)
    //   }
    // }, [isDragging, setIsDragging])

    // useEffect(() => {
    //   setIsEditorActive(true)
    // }, [setIsEditorActive])

    const handleMenuOpen = useCallback(() => {
      setIsMenuOpen(true)
    }, [])

    const handleMenuClose = useCallback(() => {
      setIsMenuOpen(false)
    }, [])

    return (
      <>
        <Box ref={editorRef}>
          <OutsideClickHandler onOutsideClick={onOutsideClick}>
            <Box id={id} className={styles.editor__container} onClick={() => setHasFocus(true)}>
              <EditorComposer readonly={readOnly || disabled} jsonState={jsonState}>
                {!readOnly && (
                  <ToolbarPlugin
                    setIsDragging={setIsDragging}
                    containerSize={containerSize}
                    // isDragging={isTaskDragging}
                    editorRef={editorRef}
                  />
                )}

                <Box className={styles.editor__inner}>
                  <RichTextPlugin
                    ErrorBoundary={LexicalErrorBoundary}
                    contentEditable={<ContentEditable id={id} className={styles.editor__input} />}
                    placeholder=""
                  />

                  <MentionsPlugin
                    menuOpen={ismenuOpen}
                    menuComponent={Menu}
                    menuItemComponent={MenuItem}
                    onMenuOpen={handleMenuOpen}
                    onMenuClose={handleMenuClose}
                    setIsStopEditorUpdate={setIsStopEditorUpdate}
                  />
                  <CodeHighlightPlugin />

                  <ListPlugin />
                  <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
                  <OnChangePlugin
                    onChange={(editor) => {
                      onChange(
                        editor,
                        setPlainText,
                        setJsonState,
                        setIsClear,
                        setIsHasValue,
                        onUpdate,
                      )
                    }}
                  />
                </Box>
                <AutoFocusPlugin />
                <ListMaxIndentLevelPlugin maxDepth={7} />
                <TabIndentationPlugin />
                <TableOfContentPlugin />
                <ImagesPlugin captionsEnabled={true} />
                <HistoryPlugin externalHistoryState={historyState} />
              </EditorComposer>
            </Box>
          </OutsideClickHandler>
        </Box>

        {!!onSave && (
          <Box display="flex" justifyContent="space-between" marginTop="10px" alignItems="center">
            <Text
              as={Button}
              onClick={onCancel}
              kind="borderless"
              marginLeft="15px"
              fontSize="fs-14"
              vertSize={20}
              styles={{
                "& span": { color: "var(--gray-60)" },
                "& span:hover": { color: "var(--gray-80)" },
              }}
              disabled={disabled}
            >
              Отмена
            </Text>
            <Button
              onClick={handleSave}
              kind="primary"
              vertSize={32}
              disabled={disabled || !isHasValue}
              loading={disabled}
              loadingMessage="Сохранение..."
            >
              Сохранить
            </Button>
          </Box>
        )}
      </>
    )
  },
)

CommentEditor.displayName = "EditorComp"
